/**
 * C2: `?next=` after sign-in. Only a relative path inside the same tenant is honoured, so the
 * login page can never be used as an open redirect (`//evil.com`, `https://…`, `/\evil`,
 * another tenant's `/t/<other>/…`).
 */
export function safeNextPath(next: string | null | undefined, tenantSlug: string): string | null {
  if (!next || !tenantSlug) return null;
  // Must be a plain absolute path: not protocol-relative, no backslashes, no control chars.
  if (!next.startsWith("/") || next.startsWith("//") || /[\\\u0000-\u001f]/.test(next)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(next, "http://same.origin");
  } catch {
    return null;
  }
  if (url.origin !== "http://same.origin") return null;
  const prefix = `/t/${tenantSlug}`;
  if (url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`)) return null;
  // Never bounce back to the login page itself.
  if (url.pathname === `${prefix}/login`) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * `/t/<slug>/login?reason=expired&next=<current path>` for a session that could not be renewed.
 * H3: `reason=other_device` when it was signed out to let the account sign in on another device.
 * H4: `reason=suspended` when the school suspended the account.
 * W-29: `reason=not_approved` when the email was removed from the school's approved list.
 */
export function expiredLoginPath(
  tenantSlug: string,
  currentPath: string,
  reason: "expired" | "other_device" | "suspended" | "not_approved" = "expired",
): string {
  const params = new URLSearchParams({ reason });
  const next = safeNextPath(currentPath, tenantSlug);
  if (next) params.set("next", next);
  return `/t/${tenantSlug}/login?${params.toString()}`;
}

/** The tenant slug of a `/t/<slug>/…` SPA path, if any. */
export function tenantSlugFromPath(pathname: string): string | null {
  const match = /^\/t\/([^/?#]+)/.exec(pathname);
  if (!match) return null;
  // SPA-13: a malformed escape (`/t/%E0%A4/…`) is "no slug", not a URIError that strands the
  // expired-session redirect.
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}
